export const CLIENT_ID_HEADER = "x-worksuite-client-id";
export const QUEUE_META_HEADER = "x-worksuite-queue-meta";
export const OPERATION_KIND_HEADER = "x-worksuite-operation-kind";
export const READ_CACHE_KEY_HEADER = "x-worksuite-read-cache-key";

export const START_FLOATING_TIMER_MUTATION = "StartFloatingTimer";
export const STOP_FLOATING_TIMER_MUTATION = "StopFloatingTimer";
export const VERIFY_CONTAINER_MUTATION = "SubmitVerifyContainer";
export const VERIFY_ORDER_MUTATION = "SubmitOrderVerification";
export const RECORD_ORDER_PULLS_MUTATION = "RecordOrderPulls";
export const UNCONFIRMED_ORDER_VERIFICATION_CODE = "ORDER_VERIFICATION_UNCONFIRMED";
export const CONCURRENT_WRITE_CONFLICT_CODE = "CONCURRENT_WRITE_CONFLICT";
export const QUEUE_RETENTION_MINUTES = 7 * 24 * 60;

export const OFFLINE_QUEUE_APP = {
	AVAILABILITY: "availability",
	ENGAGE: "engage",
	EVENTS: "events",
	MATERIAL: "material",
	SHRINK: "shrink",
	UNKNOWN: "unknown",
	VERIFY: "verify"
} as const;

export type OfflineQueueApp = (typeof OFFLINE_QUEUE_APP)[keyof typeof OFFLINE_QUEUE_APP];

export const OFFLINE_QUEUE_APPS = Object.values(OFFLINE_QUEUE_APP) as OfflineQueueApp[];

export interface QueueEntryMeta {
	app: OfflineQueueApp;
	detail?: string;
	enqueuedAt: number;
	labelKey: string;
	reconciliationId?: string;
}

export const isOfflineQueueApp = (value: unknown): value is OfflineQueueApp =>
	typeof value === "string" && (OFFLINE_QUEUE_APPS as string[]).includes(value);

export const decodeQueueMeta = (raw: string | null | undefined): QueueEntryMeta | undefined => {
	if (!raw) return undefined;
	let parsed: unknown;
	try {
		parsed = JSON.parse(decodeURIComponent(raw));
	} catch {
		return undefined;
	}
	if (!parsed || typeof parsed !== "object") return undefined;
	const { app, detail, enqueuedAt, labelKey, reconciliationId } = parsed as {
		app?: unknown;
		detail?: unknown;
		enqueuedAt?: unknown;
		labelKey?: unknown;
		reconciliationId?: unknown;
	};
	if (!isOfflineQueueApp(app)) return undefined;
	if (typeof enqueuedAt !== "number" || !Number.isFinite(enqueuedAt)) return undefined;
	if (typeof labelKey !== "string" || labelKey.length === 0) return undefined;
	if (reconciliationId !== undefined && typeof reconciliationId !== "string") return undefined;
	if (detail !== undefined && typeof detail !== "string") return undefined;
	return {
		app,
		enqueuedAt,
		labelKey,
		...(reconciliationId !== undefined ? { reconciliationId } : {}),
		...(detail !== undefined ? { detail } : {})
	};
};

// Header values are ByteStrings — anything outside Latin-1 throws on Headers.set, so percent-encode.
const encodeQueueMeta = (meta: QueueEntryMeta): string => encodeURIComponent(JSON.stringify(meta));

export default encodeQueueMeta;
